import React, { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Check, FileText, Lock, ArrowRight, Loader2 } from "lucide-react";

const FEATURES = [
  "CSV of every paid crypto invoice with USD-at-receipt cost basis",
  "Pre-filled IRS Form 8949 (crypto disposals) for paid invoices",
  "One-click filing-ready bundle delivered before April 15",
  "Founder pricing — $29 today instead of $99 list",
  "Refundable anytime before delivery, no questions asked",
];

const TaxSeasonPack = () => {
  const [params] = useSearchParams();
  const [stats, setStats] = useState(null);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [waitlisting, setWaitlisting] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  const loadStats = useCallback(async () => {
    try {
      const r = await api.get("/tax-pack/stats");
      setStats(r.data);
    } catch (e) {
      // Fall back to baseline numbers so the page still renders.
      // We log so prod stats-endpoint failures are still discoverable.
      console.warn("Tax pack stats unavailable, using defaults:", e);
      setStats({
        paid_count: 0,
        waitlist_count: 0,
        target: 100,
        deposit_usd: 29,
        list_price_usd: 99,
      });
    }
  }, []);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  // Post-redirect sync (?tp_session_id=cs_...)
  useEffect(() => {
    const sid = params.get("tp_session_id");
    if (!sid) return;
    (async () => {
      try {
        const r = await api.post("/tax-pack/sync", { session_id: sid });
        if (r.data?.paid) {
          setConfirmed(true);
          toast.success("Pre-order confirmed. We'll email you in March.");
          loadStats();
        }
      } catch (e) {
        // Webhook is authoritative — log only for prod observability.
        console.warn("Tax pack session sync failed (webhook will reconcile):", e);
      }
    })();
  }, [params, loadStats]);

  const cancelled = params.get("tp_cancelled") === "1";
  useEffect(() => {
    if (cancelled) {
      toast.message("Checkout cancelled.", { description: "No charge was made." });
    }
  }, [cancelled]);

  const handlePreorder = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Email is required");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/tax-pack/preorder", {
        email: email.trim(),
        name: name.trim() || null,
      });
      if (r.data?.url) {
        window.location.href = r.data.url;
      } else {
        toast.error("Could not start checkout");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Pre-order failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleWaitlist = async () => {
    if (!email.trim()) {
      toast.error("Add your email first");
      return;
    }
    setWaitlisting(true);
    try {
      await api.post("/tax-pack/waitlist", {
        email: email.trim(),
        name: name.trim() || null,
      });
      toast.success("You're on the waitlist.");
      loadStats();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not join waitlist");
    } finally {
      setWaitlisting(false);
    }
  };

  const paid = stats?.paid_count ?? 0;
  const target = stats?.target ?? 100;
  const pct = Math.min(100, Math.round((paid / target) * 100));

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-zinc-100" data-testid="tax-pack-page">
      {/* Top nav */}
      <header className="border-b border-[#1A1A1A]">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="font-display tracking-tight text-lg" data-testid="tax-pack-logo">
            Quill
          </Link>
          <Link
            to="/pricing"
            className="text-xs uppercase tracking-[0.22em] text-zinc-400 hover:text-white"
            data-testid="tax-pack-pricing-link"
          >
            Pricing
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6">
        {/* Hero */}
        <section className="grid md:grid-cols-2 gap-16 py-20 border-b border-[#1A1A1A]">
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-amber-300/80 mb-6" data-testid="tax-pack-overline">
              Quill · Tax Season Pack · 2025
            </div>
            <h1 className="font-display text-5xl lg:text-6xl tracking-tight leading-[1.02]">
              Stop dreading
              <br />
              <span className="text-zinc-500">April 15.</span>
            </h1>
            <p className="text-zinc-400 text-base leading-relaxed mt-8 max-w-md">
              Quill already knows every invoice you got paid in crypto, the wallet it
              landed in, and the USD price at the moment of receipt. We turn that into
              a filing-ready 8949 bundle — delivered to your inbox before April 15.
            </p>

            <div className="mt-10 flex items-baseline gap-3">
              <div className="font-mono text-4xl tracking-tight" data-testid="tax-pack-price">
                ${stats?.deposit_usd ?? 29}
              </div>
              <div className="text-sm text-zinc-500 line-through font-mono">
                ${stats?.list_price_usd ?? 99}
              </div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-amber-300/80 ml-1">
                Founder price · refundable
              </div>
            </div>
          </div>

          {/* Pre-order form */}
          <div className="border border-[#1A1A1A] bg-[#0F0F0F] p-8" data-testid="tax-pack-form-card">
            {confirmed ? (
              <div className="space-y-6" data-testid="tax-pack-confirmed">
                <div className="w-12 h-12 rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center">
                  <Check size={22} className="text-emerald-300" strokeWidth={2.5} />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-300/80">
                    Pre-order confirmed
                  </div>
                  <h2 className="font-display text-2xl mt-2">You're locked in.</h2>
                  <p className="text-sm text-zinc-400 mt-3 leading-relaxed">
                    We'll email you in early March with your 8949 bundle and CSV. If
                    anything changes, your $29 deposit is refundable anytime.
                  </p>
                </div>
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 text-sm text-white hover:text-zinc-300"
                  data-testid="tax-pack-back-home"
                >
                  Back to Quill <ArrowRight size={14} />
                </Link>
              </div>
            ) : (
              <form onSubmit={handlePreorder} className="space-y-5">
                <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">
                  Reserve your spot
                </div>
                <input
                  type="email"
                  required
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-[#0A0A0A] border border-[#1A1A1A] focus:border-zinc-500 outline-none px-4 py-3 text-sm font-mono"
                  data-testid="tax-pack-email-input"
                />
                <input
                  type="text"
                  placeholder="Name (optional)"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full bg-[#0A0A0A] border border-[#1A1A1A] focus:border-zinc-500 outline-none px-4 py-3 text-sm"
                  data-testid="tax-pack-name-input"
                />

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="tax-pack-preorder-btn"
                  className="w-full bg-white text-black text-sm font-medium px-4 py-3 hover:bg-zinc-200 disabled:opacity-50 inline-flex items-center justify-center gap-2 transition-colors"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={14} className="animate-spin" /> Opening checkout…
                    </>
                  ) : (
                    <>
                      Pre-order — ${stats?.deposit_usd ?? 29} deposit
                      <ArrowRight size={14} />
                    </>
                  )}
                </button>

                <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-zinc-500">
                  <Lock size={11} /> Refundable in the same chain anytime
                </div>

                <div className="pt-4 border-t border-[#1A1A1A] text-center">
                  <button
                    type="button"
                    onClick={handleWaitlist}
                    disabled={waitlisting}
                    data-testid="tax-pack-waitlist-btn"
                    className="text-xs text-zinc-400 hover:text-white underline underline-offset-4"
                  >
                    {waitlisting ? "Adding…" : "Or join the free waitlist →"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </section>

        {/* Social proof counter */}
        <section className="py-12 border-b border-[#1A1A1A]" data-testid="tax-pack-counter-section">
          <div className="flex items-baseline justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">
                Founder pre-orders
              </div>
              <div className="font-mono text-2xl mt-2" data-testid="tax-pack-counter">
                {paid} <span className="text-zinc-600">/ {target}</span>
              </div>
            </div>
            <div className="text-xs text-zinc-500" data-testid="tax-pack-waitlist-size">
              + {stats?.waitlist_count ?? 0} on waitlist
            </div>
          </div>
          <div className="h-px bg-[#1A1A1A] relative overflow-hidden">
            <div
              className="absolute inset-y-0 left-0 bg-amber-300/70"
              style={{ width: `${pct}%`, height: "1px" }}
              data-testid="tax-pack-progress-bar"
            />
          </div>
        </section>

        {/* What's inside */}
        <section className="py-20 border-b border-[#1A1A1A]">
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-4">
            What you get
          </div>
          <h2 className="font-display text-3xl tracking-tight mb-12">
            Filing-ready, not spreadsheet-ready.
          </h2>
          <ul className="grid md:grid-cols-2 gap-x-12 gap-y-4 max-w-4xl">
            {FEATURES.map((f, i) => (
              <li
                key={f}
                className="flex items-start gap-3 text-sm text-zinc-300"
                data-testid={`tax-pack-feature-${i}`}
              >
                <Check size={16} className="text-amber-300/80 mt-0.5 shrink-0" strokeWidth={2.5} />
                <span>{f}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* FAQ-lite */}
        <section className="py-20 border-b border-[#1A1A1A]">
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-4">
            Common questions
          </div>
          <div className="grid md:grid-cols-2 gap-x-12 gap-y-10 max-w-4xl">
            <div>
              <div className="font-display text-lg mb-2">What if I'm not in the US?</div>
              <p className="text-sm text-zinc-400 leading-relaxed">
                We deliver the raw CSV (per-invoice USD-at-receipt cost basis) to every
                pre-order regardless of country. Form 8949 is US-specific; non-US filers
                hand the CSV to their accountant.
              </p>
            </div>
            <div>
              <div className="font-display text-lg mb-2">Is the deposit really refundable?</div>
              <p className="text-sm text-zinc-400 leading-relaxed">
                Yes. Email us before delivery and we refund the full $29 to the same
                wallet you paid from — same chain, same address. No forms, no clawback
                fine print.
              </p>
            </div>
            <div>
              <div className="font-display text-lg mb-2">When does the bundle ship?</div>
              <p className="text-sm text-zinc-400 leading-relaxed">
                First week of March 2026. That leaves a six-week buffer ahead of the
                April 15 deadline.
              </p>
            </div>
            <div>
              <div className="font-display text-lg mb-2">Do I need a Quill account?</div>
              <p className="text-sm text-zinc-400 leading-relaxed">
                No — you can pre-order from this page. You'll get an account-creation
                link with your bundle so we can pull invoice history from the wallet
                you'll point us at.
              </p>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-10 flex items-center justify-between text-xs text-zinc-500">
          <div className="flex items-center gap-2">
            <FileText size={12} /> Drafted with Quill
          </div>
          <div className="flex gap-6">
            <Link to="/terms" className="hover:text-white">Terms</Link>
            <Link to="/privacy" className="hover:text-white">Privacy</Link>
            <Link to="/refunds" className="hover:text-white">Refunds</Link>
          </div>
        </footer>
      </main>
    </div>
  );
};

export default TaxSeasonPack;
